// Signup.js - Soulemap signup
import React, { useState, useEffect } from 'react';
import { isInNativeApp } from './platform';
import { nativeAuthAvailable, nativeSocialSignIn } from './nativeAuth';
import { 
  createUserWithEmailAndPassword, 
  updateProfile, 
  GoogleAuthProvider, 
  OAuthProvider, 
  FacebookAuthProvider,
  signInWithPopup, 
  getRedirectResult
} from 'firebase/auth';
import { auth, db } from './firebase';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { useNavigate, useLocation } from 'react-router-dom';
import { events } from './analytics';

function Signup() {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [socialLoading, setSocialLoading] = useState('');
  const [isEmailFormExpanded, setIsEmailFormExpanded] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  
  const navigate = useNavigate();
  const location = useLocation();

  // Get redirect URL from query params
  const searchParams = new URLSearchParams(location.search);
  const redirectUrl = searchParams.get('redirect') || '/dashboard';

  // Check screen size for responsive behavior
  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth < 1024); // lg breakpoint
    };
    
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  // Handle redirect result on component mount (for mobile OAuth flow)
  useEffect(() => {
    const handleRedirectResult = async () => {
      try {
        const result = await getRedirectResult(auth);
        if (result && result.user) {
          
          
          // Check if this is a new user
          const isNewUser = result.user.metadata.creationTime === result.user.metadata.lastSignInTime;
          
          if (isNewUser) {
            // New user - record legal acceptance
            await recordLegalAcceptance(result.user.uid, result.user.email);
            
          }
          
          window.scrollTo(0, 0);

          // Navigate after successful redirect signup
          navigate(redirectUrl);
        }
      } catch (error) {
        console.error('Redirect result error:', error);
        // handleSocialSignupError(error, 'OAuth');
      }
    };

    handleRedirectResult();
  }, [navigate, redirectUrl]);

  // Record user's legal acceptance in Firestore
  const recordLegalAcceptance = async (userId, userEmail) => {
    try {
      await setDoc(doc(db, 'users', userId), {
        email: userEmail,
        legalAcceptance: {
          termsOfService: {
            accepted: true,
            timestamp: serverTimestamp(),
            version: '1.0'
          },
          privacyPolicy: {
            accepted: true,
            timestamp: serverTimestamp(),
            version: '1.0'
          },
          donationAgreement: {
            accepted: true,
            timestamp: serverTimestamp(),
            version: '1.0'
          }
        },
        createdAt: serverTimestamp()
      }, { merge: true });
    } catch (error) {
      console.error('Error recording legal acceptance:', error);
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    
    setIsLoading(true);
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);

      // Update the user's display name with first and last name
      await updateProfile(userCredential.user, {
        displayName: `${firstName} ${lastName}`
      });

      // Record legal acceptance
      await recordLegalAcceptance(userCredential.user.uid, email);
      events.signUp('email');
      
      window.scrollTo(0, 0);

      navigate(redirectUrl);
    } catch (err) {
      alert(err.message);
    } finally {
      setIsLoading(false);
    }
  };

const handleSocialLoginError = (error, provider) => {
    console.error(`${provider} login error:`, error);
    
    // Handle specific error cases
    if (error.code === 'auth/popup-closed-by-user') {
      
    } else if (error.code === 'auth/popup-blocked') {
      alert('Popup was blocked by browser. Please allow popups for this site and try again.');
    } else if (error.code === 'auth/account-exists-with-different-credential') {
      alert('An account already exists with the same email address but different sign-in credentials. Please try signing in with a different method.');
    } else if (error.code === 'auth/operation-not-allowed') {
      alert(`${provider} sign-in is not enabled. Please contact support.`);
    } else if (error.code === 'auth/unauthorized-domain') {
      alert('This domain is not authorized for authentication. Please contact support.');
    } else if (error.code === 'auth/cancelled-popup-request') {
      
    } else if (error.code === 'auth/network-request-failed') {
      alert('Network error. Please check your connection and try again.');
    } else if (error.code === 'auth/timeout') {
      alert('Sign-in request timed out. Please try again.');
    } else {
      alert(`${provider} login failed: ${error.message}`);
    }
  };

  const handleSocialSignup = async (provider) => {
    setSocialLoading(provider);

    // Inside the native app: real system dialogs instead of web popups.
    if (isInNativeApp()) {
      try {
        if (!nativeAuthAvailable()) throw new Error('Please update the SouleMap app to use this sign-in.');
        const result = await nativeSocialSignIn(auth, provider);
        const user = result.user;
        const isNewUser = user.metadata.creationTime === user.metadata.lastSignInTime;
        if (isNewUser) {
          await recordLegalAcceptance(user.uid, user.email);
        }
        (isNewUser ? events.signUp : events.login)(provider);
        window.scrollTo(0, 0);
        navigate(redirectUrl);
      } catch (error) {
        if (error?.code !== 'auth/popup-closed-by-user' && !/canceled|cancelled/i.test(error?.message || '')) {
          handleSocialLoginError(error, provider);
        }
      } finally {
        setSocialLoading('');
      }
      return;
    }

    try {
      let authProvider;
      
      if (provider === 'Google') {
        authProvider = new GoogleAuthProvider();
        authProvider.addScope('profile');
        authProvider.addScope('email');
      } else if (provider === 'Apple') {
        authProvider = new OAuthProvider('apple.com');
        // Apple Sign-In scopes are more restrictive
        // Only add scopes that are properly configured
        authProvider.addScope('email');
        // Note: 'name' scope for Apple requires special handling and might cause issues
        // Only add if properly configured in your Apple Developer account
        
        // Set custom parameters for Apple Sign-In if needed
        authProvider.setCustomParameters({
          locale: 'en'
        });
      } else if (provider === 'Facebook') {
          authProvider = new FacebookAuthProvider();
          authProvider.addScope('email');
          authProvider.addScope('public_profile');
          
          // Set custom parameters for Facebook if needed
          authProvider.setCustomParameters({
            display: 'popup'
          });
      }

      const signInPromise = signInWithPopup(auth, authProvider);
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Sign-in timed out')), 60000) // 60 second timeout
      );

      const result = await Promise.race([signInPromise, timeoutPromise]);
      const user = result.user;
      
      
      // Check if this is a new user or existing user
      const isNewUser = result.user.metadata.creationTime === result.user.metadata.lastSignInTime;
      
      if (isNewUser) {
        // New user - record legal acceptance
        await recordLegalAcceptance(user.uid, user.email);
        
      }
      
      // For Apple Sign-In, handle the response
      if (provider === 'Apple') {
        
        
        // Check if email is private relay
        if (user.email?.includes('@privaterelay.appleid.com')) {
          
        }
        
        // Apple might provide additional info in the credential
        const credential = OAuthProvider.credentialFromResult(result);
        if (credential) {
          
        }
      }
      
      window.scrollTo(0, 0);

      // Handle redirect after successful login
      navigate(redirectUrl);
      
    } catch (error) {
      // Handle timeout specifically
      if (error.message === 'Sign-in timed out') {
        alert('Sign-in took too long. Please try again.');
      } else {
        handleSocialLoginError(error, provider);
      }
    } finally {
      setSocialLoading('');
    }
  };

  const handleBackToMap = () => {
    navigate('/map');
  };

  const handleBackToLogin = () => {
    if (redirectUrl !== '/dashboard') {
      navigate(`/login?redirect=${encodeURIComponent(redirectUrl)}`);
    } else {
      navigate('/login');
    }
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ background: 'radial-gradient(ellipse at 50% 0%, #131022 0%, #05040d 70%)' }}>
      {/* Top Navigation - Squarespace style */}
      <div className="flex justify-between items-center p-8 md:p-12">
        {/* Back Arrow */}
        <button 
          onClick={handleBackToMap}
          className="w-10 h-10 rounded-full bg-white/10 border border-white/25 hover:border-[#cbab6a]/70 hover:bg-white/20 flex items-center justify-center transition-all duration-300"
        >
          <svg className="w-4 h-4 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M15 19l-7-7 7-7" />
          </svg>
        </button>

        {/* Log In */}
        <button 
          onClick={handleBackToLogin}
          className="text-xs text-white hover:opacity-70 transition-all duration-300 uppercase tracking-[0.2em] font-light"
        >
          Log In
        </button>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center px-8 md:px-12 pb-16">
        <div className="w-full max-w-6xl">
          <h1 className="text-5xl md:text-6xl font-extralight text-center text-white mb-4 tracking-wide">
            Get Started
          </h1>
          <p className="text-gray-500 text-sm text-center mb-16 uppercase tracking-[0.2em] font-light">
            To Create A Memorial for your Loved Ones
          </p>

          {/* Form Layout */}
          <div className="flex flex-col lg:flex-row items-start justify-center gap-12 lg:gap-24">
            {/* Left Side - Email Form (bottom on mobile, collapsible) */}
            <div className="w-full lg:w-96 order-3 lg:order-1">
              {/* Mobile Toggle Button */}
              {isMobile && (
                <button
                  type="button"
                  onClick={() => setIsEmailFormExpanded(!isEmailFormExpanded)}
                  className="w-full flex items-center justify-between px-6 py-4 mb-6 rounded-full border border-gray-800 hover:border-[#cbab6a]/60 transition-all duration-300 bg-transparent group"
                >
                  <span className="text-white text-xs font-light uppercase tracking-[0.15em] group-hover:text-gray-300 transition-all duration-300">
                    Sign up with Email
                  </span>
                  <svg 
                    className={`w-4 h-4 text-gray-500 transition-transform duration-300 ${isEmailFormExpanded ? 'rotate-180' : ''}`} 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
              )}

              {/* Email Form - Always visible on desktop, collapsible on mobile */}
              <div className={`${isMobile ? (isEmailFormExpanded ? 'block' : 'hidden') : 'block'} transition-all duration-500`}>
                <form onSubmit={handleSignup} className="space-y-8">
                  {/* First Name */}
                  <div>
                    <label className="block text-xs text-gray-500 uppercase tracking-[0.2em] mb-4 font-light">
                      First Name
                    </label>
                    <input
                      type="text"
                      placeholder="John"
                      value={firstName}
                      onChange={e => setFirstName(e.target.value)}
                      className="w-full pb-3 border-0 border-b border-gray-700/70 bg-transparent text-white placeholder-gray-600 focus:outline-none focus:border-[#e5c883] transition-all duration-500 font-light"
                      required
                    />
                  </div>

                  {/* Last Name */}
                  <div>
                    <label className="block text-xs text-gray-500 uppercase tracking-[0.2em] mb-4 font-light">
                      Last Name
                    </label>
                    <input
                      type="text"
                      placeholder="Doe"
                      value={lastName}
                      onChange={e => setLastName(e.target.value)}
                      className="w-full pb-3 border-0 border-b border-gray-700/70 bg-transparent text-white placeholder-gray-600 focus:outline-none focus:border-[#e5c883] transition-all duration-500 font-light"
                      required
                    />
                  </div>

                  {/* Email */}
                  <div>
                    <label className="block text-xs text-gray-500 uppercase tracking-[0.2em] mb-4 font-light">
                      Email Address
                    </label>
                    <input
                      type="email"
                      placeholder="name@example.com"
                      value={email}
                      onChange={e => setEmail(e.target.value)}
                      className="w-full pb-3 border-0 border-b border-gray-700/70 bg-transparent text-white placeholder-gray-600 focus:outline-none focus:border-[#e5c883] transition-all duration-500 font-light"
                      required
                    />
                  </div>

                  {/* Password */}
                  <div>
                    <label className="block text-xs text-gray-500 uppercase tracking-[0.2em] mb-4 font-light">
                      Password
                    </label>
                    <div className="relative">
                      <input
                        type={showPassword ? "text" : "password"}
                        placeholder="Enter password"
                        value={password}
                        onChange={e => setPassword(e.target.value)}
                        className="w-full pb-3 pr-10 border-0 border-b border-gray-700/70 bg-transparent text-white placeholder-gray-600 focus:outline-none focus:border-[#e5c883] transition-all duration-500 font-light"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-0 bottom-3 text-gray-500 hover:text-white transition-all duration-300"
                      >
                        {showPassword ? (
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                        ) : (
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                          </svg>
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Terms Notice for Email Signup */}
                  <div className="mt-8">
                    <p className="text-xs text-gray-500 font-light">
                      By creating an account, you agree to our{' '}
                      <a href="/legal/terms" target="_blank" className="text-gray-400 hover:text-white transition-all duration-300">Terms of Service</a>,{' '}
                      <a href="/legal/privacy" target="_blank" className="text-gray-400 hover:text-white transition-all duration-300">Privacy Policy</a>, and{' '}
                      <a href="/legal/donation" target="_blank" className="text-gray-400 hover:text-white transition-all duration-300">Donation Agreement</a>
                    </p>
                  </div>

                  {/* Sign Up Button */}
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="w-full rounded-full bg-white hover:bg-[#ffedc4] disabled:bg-gray-700 disabled:text-gray-500 text-black py-4 text-xs font-medium uppercase tracking-[0.2em] transition-all duration-300 disabled:cursor-not-allowed mt-8 hover:shadow-[0_0_24px_rgba(255,215,140,0.35)]"
                  >
                    {isLoading ? 'Creating Account...' : 'Create Account'}
                  </button>
                </form>
              </div>
            </div>

            {/* Divider - Hide on mobile when email form is collapsed */}
            <div className={`flex lg:flex-col items-center justify-center w-full lg:w-auto order-2 ${isMobile && !isEmailFormExpanded ? 'hidden' : ''}`}>
              <div className="hidden lg:block w-px h-40 bg-gray-800"></div>
              <div className="lg:hidden w-32 h-px bg-gray-800"></div>
              <span className="px-6 py-3 text-xs text-gray-500 uppercase tracking-[0.2em] font-light">OR</span>
              <div className="hidden lg:block w-px h-40 bg-gray-800"></div>
              <div className="lg:hidden w-32 h-px bg-gray-800"></div>
            </div>

            {/* Right Side - Social Options (hidden in the native app: popup
                OAuth does not work in an embedded web view) */}
            <div className="w-full lg:w-96 space-y-4 order-1 lg:order-3">
              {/* Social signup heading - visible only on mobile when email is collapsed */}
              {isMobile && !isEmailFormExpanded && (
                <p className="text-xs text-gray-500 uppercase tracking-[0.2em] font-light text-center mb-6">
                  Or continue with
                </p>
              )}

              {/* Google Signup */}
              <button
                onClick={() => handleSocialSignup('Google')}
                disabled={socialLoading !== '' || isLoading}
                className="w-full flex items-center px-6 py-4 rounded-full border border-gray-800 hover:border-[#cbab6a]/60 transition-all duration-300 bg-transparent disabled:opacity-50 disabled:cursor-not-allowed group"
              >
                <div className="w-8 flex items-center justify-center">
                  {socialLoading === 'Google' ? (
                    <div className="w-5 h-5 border-2 border-gray-600 border-t-white rounded-full animate-spin"></div>
                  ) : (
                    <svg className="w-5 h-5" viewBox="0 0 24 24">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                  )}
                </div>
                <span className="flex-1 text-center text-white text-xs font-light uppercase tracking-[0.15em] group-hover:text-gray-300 transition-all duration-300">
                  Continue with Google
                </span>
              </button>

              {/* Apple Signup */}
              <button
                onClick={() => handleSocialSignup('Apple')}
                disabled={socialLoading !== '' || isLoading}
                className="w-full flex items-center px-6 py-4 rounded-full border border-gray-800 hover:border-[#cbab6a]/60 transition-all duration-300 bg-transparent disabled:opacity-50 disabled:cursor-not-allowed group"
              >
                <div className="w-8 flex items-center justify-center">
                  {socialLoading === 'Apple' ? (
                    <div className="w-5 h-5 border-2 border-gray-600 border-t-white rounded-full animate-spin"></div>
                  ) : (
                    <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                    </svg>
                  )}
                </div>
                <span className="flex-1 text-center text-white text-xs font-light uppercase tracking-[0.15em] group-hover:text-gray-300 transition-all duration-300">
                  Continue with Apple
                </span>
              </button>

              {/* Facebook Signup */}

              {/* Terms Notice for Social Signup */}
              <div className="mt-8">
                <p className="text-xs text-gray-500 font-light">
                  By creating an account, you agree to our{' '}
                  <a href="/legal/terms" target="_blank" className="text-gray-400 hover:text-white transition-all duration-300">Terms of Service</a>,{' '}
                  <a href="/legal/privacy" target="_blank" className="text-gray-400 hover:text-white transition-all duration-300">Privacy Policy</a>, and{' '}
                  <a href="/legal/donation" target="_blank" className="text-gray-400 hover:text-white transition-all duration-300">Donation Agreement</a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Section - Fixed at bottom */}
      <div className="text-center pb-8 px-8 mt-16">
        {/* Security Notice */}
        <p className="text-xs text-gray-600 font-light">
          Secure Login with reCAPTCHA subject to Google{' '}
          <a href="https://policies.google.com/terms?hl=en" target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-gray-400 transition-all duration-300">Terms</a> &{' '}
          <a href="https://policies.google.com/privacy?hl=en" target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-gray-400 transition-all duration-300">Privacy</a>
        </p>
      </div>
    </div>
  );
}

export default Signup;
